<script setup lang="ts">
import { useEditor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Image from '@tiptap/extension-image'
import { Mark, mergeAttributes } from '@tiptap/core'
import { watch, onBeforeUnmount, computed, ref } from 'vue'
import { UPLOAD_API_URL, STATIC_FILE_URL } from '../config'
import TermEditor from './TermEditor.vue'

// 自定义词条 Mark
const TermMark = Mark.create({
  name: 'term',
  
  addAttributes() {
    return {
      id: {
        default: null,
      },
      description: {
        default: '',
      },
    }
  },
  
  parseHTML() {
    return [
      {
        tag: 'span[data-term]',
      },
    ]
  },
  
  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes({ 'data-term': 'true', class: 'term-mark' }, HTMLAttributes), 0]
  },
  
})

interface Props {
  modelValue: string
  placeholder?: string
  saving?: boolean
  loading?: boolean
  showSaveButton?: boolean
  noMaxWidth?: boolean
  uploadPath?: string
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: '开始写作...',
  saving: false,
  loading: false,
  showSaveButton: true,
  noMaxWidth: false,
  uploadPath: '/api/upload/startup'
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
  'insertImage': []
  'save': []
}>()

// 内容处理：如果是 HTML 则直接使用，否则作为纯文本
const processContent = (content: string): string => {
  if (!content) return ''
  // 简单判断是否是 HTML，如果是则直接返回
  if (content.trim().startsWith('<')) {
    return content
  }
  // 否则作为纯文本处理（包裹在 p 标签中）
  return `<p>${content}</p>`
}

// 词条相关状态 - 统一使用一个抽屉
const showTermDrawer = ref(false)
const drawerMode = ref<'add' | 'edit' | 'view'>('add') // 抽屉模式：添加/编辑/查看
const drawerTermId = ref<string>('') // 当前词条ID
const drawerTermName = ref('') // 词条名称
const drawerContent = ref('') // 词条内容
const selectedText = ref('') // 右键选中的文本
const termLoading = ref(false)

// 图片预览状态
const showImagePreview = ref(false)
const previewImageUrl = ref('')

// 打开图片预览
const openImagePreview = (url: string) => {
  previewImageUrl.value = url
  showImagePreview.value = true
}

// 关闭图片预览
const closeImagePreview = () => {
  showImagePreview.value = false
  previewImageUrl.value = ''
}

// 确认弹框状态
const confirmDialog = ref({ show: false, title: '', message: '', onConfirm: () => {} })

// 显示确认弹框
const showConfirm = (title: string, message: string, onConfirm: () => void) => {
  confirmDialog.value = { show: true, title, message, onConfirm }
}

// 关闭确认弹框
const closeConfirm = () => {
  confirmDialog.value.show = false
}

// 处理确认
const handleConfirm = () => {
  confirmDialog.value.onConfirm()
  confirmDialog.value.show = false
}

// 处理编辑器点击事件（图片预览）
const handleEditorClick = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  if (target.tagName === 'IMG') {
    const imgSrc = target.getAttribute('src')
    if (imgSrc) {
      openImagePreview(imgSrc)
    }
  }
}

// 计算抽屉标题
const drawerTitle = computed(() => {
  if (drawerMode.value === 'add') return drawerTermName.value || '添加词条'
  if (drawerMode.value === 'edit') return '编辑词条'
  return drawerTermName.value || '词条详情'
})

// 打开统一抽屉（添加/编辑/查看词条）
const openTermDrawer = (mode: 'add' | 'edit' | 'view', termData?: { id: string; text: string; content: string }) => {
  drawerMode.value = mode
  
  if (mode === 'add') {
    // 添加模式：使用选中的文本
    drawerTermId.value = ''
    drawerTermName.value = selectedText.value
    drawerContent.value = ''
  } else if (termData) {
    // 编辑/查看模式
    drawerTermId.value = termData.id
    drawerTermName.value = termData.text
    drawerContent.value = termData.content
  }
  
  showTermDrawer.value = true
}

// 关闭统一抽屉
const closeTermDrawer = () => {
  showTermDrawer.value = false
  drawerTermId.value = ''
  drawerTermName.value = ''
  drawerContent.value = ''
  drawerMode.value = 'add'
}

// 从Hash路由获取查询参数
const getQueryParamFromHash = (param: string): string | null => {
  const hash = window.location.hash
  const queryIndex = hash.indexOf('?')
  if (queryIndex === -1) return null
  const queryString = hash.slice(queryIndex + 1)
  const params = new URLSearchParams(queryString)
  return params.get(param)
}

// 从抽屉保存词条（支持添加和更新）
const saveTermFromDrawer = async () => {
  // 从Hash路由获取startup_id
  const startupId = getQueryParamFromHash('id')
  
  if (!startupId) {
    alert('无法获取项目ID')
    return
  }
  
  termLoading.value = true
  try {
    if (drawerMode.value === 'add') {
      // 创建新词条
      const response = await fetch(`${UPLOAD_API_URL}/api/startup/term/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          startup_id: parseInt(startupId),
          text: drawerTermName.value,
          content: drawerContent.value,
        }),
      })
      
      const result = await response.json()
      if (result.code === 1 && result.data) {
        // 添加新词条标记
        editor.value?.chain().focus().toggleMark('term', {
          id: result.data.id.toString(),
        }).run()
        
        closeTermDrawer()
        selectedText.value = ''
        // 触发词条创建事件
        window.dispatchEvent(new CustomEvent('term-created', { detail: result.data }))
      } else {
        alert(result.msg || '创建词条失败')
      }
    } else {
      // 更新现有词条
      const response = await fetch(`${UPLOAD_API_URL}/api/startup/term/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: drawerTermId.value,
          startup_id: parseInt(startupId),
          content: drawerContent.value,
        }),
      })
      
      const result = await response.json()
      if (result.code === 1) {
        closeTermDrawer()
        // 触发词条更新事件
        window.dispatchEvent(new CustomEvent('term-updated'))
      } else {
        alert(result.msg || '更新词条失败')
      }
    }
  } catch (error) {
    console.error('保存词条失败:', error)
    alert('保存词条失败，请稍后重试')
  } finally {
    termLoading.value = false
  }
}

// 执行删除词条
const executeDeleteTerm = async () => {
  if (!drawerTermId.value) return
  
  // 从Hash路由获取startup_id
  const startupId = getQueryParamFromHash('id')
  
  if (!startupId) {
    alert('无法获取项目ID')
    return
  }
  
  termLoading.value = true
  try {
    const response = await fetch(`${UPLOAD_API_URL}/api/startup/term/delete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: drawerTermId.value,
        startup_id: parseInt(startupId),
      }),
    })
    
    const result = await response.json()
    if (result.code === 1) {
      // 关闭抽屉
      const deletedId = drawerTermId.value
      closeTermDrawer()
      
      // 从编辑器内容中移除词条标记
      if (editor.value) {
        editor.value.commands.unsetMark('term')
      }
      
      // 触发词条删除事件
      window.dispatchEvent(new CustomEvent('term-deleted', { detail: { id: deletedId } }))
    } else {
      alert(result.msg || '删除词条失败')
    }
  } catch (error) {
    console.error('删除词条失败:', error)
    alert('删除词条失败，请稍后重试')
  } finally {
    termLoading.value = false
  }
}

// 从抽屉删除词条（显示确认弹框）
const deleteTermFromDrawer = () => {
  if (!drawerTermId.value) return
  showConfirm('确认删除', '确定要删除这个词条吗？删除后无法恢复。', executeDeleteTerm)
}

const editor = useEditor({
  extensions: [
    StarterKit.configure({ 
      heading: { levels: [1, 2, 3] },
      bulletList: {},
      orderedList: {},
      blockquote: {},
      codeBlock: {},
      bold: {},
      italic: {},
      strike: {},
    }),
    Image,
    TermMark,
  ],
  content: processContent(props.modelValue),
  onUpdate: ({ editor }) => {
    emit('update:modelValue', editor.getHTML())
  },
  editorProps: {
    attributes: {
      class: 'md-editor-content',
    },
    handleClick: (view, pos, event) => {
      const target = event.target as HTMLElement
      if (target && target.classList.contains('term-mark')) {
        // 优先获取 data-id，如果没有则获取 id
        const termId = target.getAttribute('data-id') || target.getAttribute('id')
        if (termId) {
          // 调用API获取词条详情并打开抽屉
          fetchTermDetailAndOpenDrawer(termId)
        }
        return true
      }
      return false
    },
  },
})

// 监听外部值变化
watch(() => props.modelValue, (newValue) => {
  if (editor.value) {
    const htmlContent = processContent(newValue)
    if (editor.value.getHTML() !== htmlContent) {
      editor.value.commands.setContent(htmlContent)
    }
  }
})

// 监听loading变化，加载完成后更新内容
watch(() => props.loading, (isLoading) => {
  if (!isLoading && editor.value && props.modelValue) {
    // 加载完成后，确保内容正确设置
    const htmlContent = processContent(props.modelValue)
    editor.value.commands.setContent(htmlContent)
  }
})

onBeforeUnmount(() => {
  editor.value?.destroy()
})

// 获取词条详情并打开抽屉
const fetchTermDetailAndOpenDrawer = async (termId: string) => {
  termLoading.value = true
  try {
    const startupId = getQueryParamFromHash('id')
    
    const response = await fetch(`${UPLOAD_API_URL}/api/startup/term/detail?id=${termId}&startup_id=${startupId}`)
    const result = await response.json()
    if (result.code === 1 && result.data) {
      openTermDrawer('view', {
        id: termId,
        text: result.data.text || '',
        content: result.data.content || '',
      })
    } else {
      alert('获取词条详情失败')
    }
  } catch (error) {
    console.error('获取词条详情失败:', error)
    alert('获取词条详情失败，请稍后重试')
  } finally {
    termLoading.value = false
  }
}

// 监听打开词条抽屉事件（从右侧词条列表点击）
const handleOpenTermDrawer = (event: CustomEvent) => {
  const termData = event.detail
  if (termData) {
    openTermDrawer('view', {
      id: termData.id,
      text: termData.text,
      content: termData.content,
    })
  }
}

// 添加事件监听
window.addEventListener('open-term-drawer', handleOpenTermDrawer as EventListener)

// 文件输入引用
const fileInputRef = ref<HTMLInputElement | null>(null)

// 触发文件选择
const triggerImageUpload = () => {
  fileInputRef.value?.click()
}

// 处理图片上传
const handleImageUpload = async (event: Event) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  if (!file) return
  
  // 创建 FormData 并上传
  const formData = new FormData()
  formData.append('file', file)
  
  try {
    // 使用配置文件中的上传 API 地址和自定义上传路径
    const response = await fetch(`${UPLOAD_API_URL}${props.uploadPath}`, {
      method: 'POST',
      body: formData,
    })
    
    if (response.ok) {
      const result = await response.json()
      if (result.code === 1 && result.data) {
        // 使用配置文件中的静态文件 URL 拼接完整图片地址
        const imageUrl = `${STATIC_FILE_URL}/${result.data.src}`
        // 上传成功，插入图片到编辑器
        editor.value?.chain().focus().setImage({ src: imageUrl }).run()
      }
    }
  } catch (error) {
    console.error('上传图片失败:', error)
    alert('图片上传失败，请稍后重试')
  }
  
  // 清空 input，允许再次选择同一张图片
  target.value = ''
}

// 处理编辑器右键菜单
const handleEditorContextMenu = (event: MouseEvent) => {
  const selection = editor.value?.state.selection
  if (!selection || selection.empty) return
  
  // 检查是否点击在已有词条上
  const target = event.target as HTMLElement
  if (target && target.classList.contains('term-mark')) {
    return // 如果点击在词条上，不显示添加菜单
  }
  
  const selected = editor.value?.state.doc.textBetween(selection.from, selection.to)
  if (selected) {
    selectedText.value = selected
    // 显示自定义右键菜单
    showContextMenu(event.clientX, event.clientY)
  }
}

// 右键菜单状态
const contextMenuVisible = ref(false)
const contextMenuX = ref(0)
const contextMenuY = ref(0)

const showContextMenu = (x: number, y: number) => {
  contextMenuX.value = x
  contextMenuY.value = y
  contextMenuVisible.value = true
}

const hideContextMenu = () => {
  contextMenuVisible.value = false
}

// 打开添加词条弹窗（右键菜单点击）
const openTermDialog = () => {
  hideContextMenu()
  openTermDrawer('add')
}

// 暴露方法给父组件
defineExpose({
  // 可以在这里暴露需要的方法
})
</script>

<template>
  <div class="md-editor" @click="hideContextMenu">
    <!-- 加载遮罩 -->
    <div v-if="loading" class="editor-loading">
      <div class="loading-spinner"></div>
      <span>正在加载中...</span>
    </div>
    
    <!-- 右键菜单 -->
    <div 
      v-if="contextMenuVisible" 
      class="editor-context-menu"
      :style="{ left: contextMenuX + 'px', top: contextMenuY + 'px' }"
    >
      <div class="context-menu-item" @click="openTermDialog">
        <span>📌</span>
        <span>添加词条</span>
      </div>
    </div>
    
    <!-- 词条抽屉（统一使用：添加/编辑/查看） -->
    <div v-if="showTermDrawer" class="term-drawer-overlay" @click.self="closeTermDrawer">
      <div class="term-drawer" :class="{ 'drawer-open': showTermDrawer }">
        <div class="term-drawer-header">
          <h3>{{ drawerTitle }}</h3>
          <button class="drawer-close-btn" @click="closeTermDrawer">
            <svg viewBox="0 0 24 24" width="20" height="20">
              <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>
        <div class="term-drawer-body">
          <TermEditor v-model="drawerContent" class="term-editor-instance" @imageClick="openImagePreview" />
        </div>
        <div class="term-drawer-footer" :class="{ 'footer-right-only': drawerMode === 'add' }">
          <button class="btn-danger" v-if="drawerMode !== 'add'" @click="deleteTermFromDrawer">删除</button>
          <div class="footer-right">
            <button class="btn-secondary" @click="closeTermDrawer">关闭</button>
            <button class="btn-primary" @click="saveTermFromDrawer" :disabled="termLoading">
              <span v-if="termLoading" class="btn-loading"></span>
              <span v-else>保存</span>
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <div class="md-toolbar">
      <button @click="editor?.chain().focus().toggleBold().run()" :class="{ active: editor?.isActive('bold') }">B</button>
      <button @click="editor?.chain().focus().toggleItalic().run()" :class="{ active: editor?.isActive('italic') }">I</button>
      <button @click="editor?.chain().focus().toggleStrike().run()" :class="{ active: editor?.isActive('strike') }">S</button>
      <span class="divider"></span>
      <button @click="editor?.chain().focus().toggleHeading({ level: 1 }).run()" :class="{ active: editor?.isActive('heading', { level: 1 }) }">H1</button>
      <button @click="editor?.chain().focus().toggleHeading({ level: 2 }).run()" :class="{ active: editor?.isActive('heading', { level: 2 }) }">H2</button>
      <button @click="editor?.chain().focus().toggleHeading({ level: 3 }).run()" :class="{ active: editor?.isActive('heading', { level: 3 }) }">H3</button>
      <span class="divider"></span>
      <button @click="editor?.chain().focus().toggleBulletList().run()" :class="{ active: editor?.isActive('bulletList') }">• 列表</button>
      <button @click="editor?.chain().focus().toggleOrderedList().run()" :class="{ active: editor?.isActive('orderedList') }">1. 列表</button>
      <span class="divider"></span>
      <button @click="editor?.chain().focus().toggleBlockquote().run()" :class="{ active: editor?.isActive('blockquote') }">引用</button>
      <button @click="editor?.chain().focus().toggleCodeBlock().run()" :class="{ active: editor?.isActive('codeBlock') }">代码</button>
      <span class="divider"></span>
      <button class="image-btn" @click="triggerImageUpload">📷</button>
      <span v-if="showSaveButton" class="divider"></span>
      <button 
        v-if="showSaveButton"
        class="save-btn" 
        :disabled="saving" 
        @click="emit('save')"
      >
        {{ saving ? '保存中...' : '保存' }}
      </button>
    </div>
    <!-- 隐藏的文件输入 -->
    <input
      ref="fileInputRef"
      type="file"
      accept="image/*"
      style="display: none"
      @change="handleImageUpload"
    />
    <EditorContent 
      :editor="editor" 
      :class="['editor-content', { 'no-max-width': noMaxWidth }]" 
      @contextmenu.prevent="handleEditorContextMenu"
      @click="handleEditorClick"
    />
    
    <!-- 图片预览弹窗 -->
    <div v-if="showImagePreview" class="image-preview-overlay" @click.self="closeImagePreview">
      <div class="image-preview-container">
        <button class="image-preview-close" @click="closeImagePreview">
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </button>
        <img :src="previewImageUrl" class="image-preview-img" />
      </div>
    </div>
    
    <!-- 确认弹框 -->
    <div v-if="confirmDialog.show" class="confirm-overlay" @click="closeConfirm">
      <div class="confirm-dialog" @click.stop>
        <div class="confirm-title">{{ confirmDialog.title }}</div>
        <div class="confirm-message">{{ confirmDialog.message }}</div>
        <div class="confirm-actions">
          <button class="btn-cancel" @click="closeConfirm">取消</button>
          <button class="btn-confirm" @click="handleConfirm">确定</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.md-editor {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  position: relative;
}

.editor-loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 100;
  gap: 0.75rem;
}

.editor-loading .loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #f0f0f0;
  border-top-color: #42b883;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.editor-loading span {
  font-size: 0.9rem;
  color: #666;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.md-toolbar {
  display: flex;
  align-items: center;
  gap: 0.15rem;
  padding: 0.35rem 0.5rem;
  background: transparent;
  border-bottom: 1px solid #f0f0f0;
  flex-wrap: wrap;
}

.md-toolbar button {
  padding: 0.25rem 0.5rem;
  border: none;
  background: transparent;
  color: #999;
  font-size: 0.75rem;
  cursor: pointer;
  border-radius: 3px;
  transition: all 0.2s;
  font-weight: 500;
}

.md-toolbar button:hover {
  background: #f5f5f5;
  color: #666;
}

.md-toolbar button.active {
  background: transparent;
  color: #42b883;
}

.md-toolbar button.save-btn {
  background: #42b883;
  color: #fff;
  padding: 0.25rem 0.75rem;
  margin-left: auto;
}

.md-toolbar button.save-btn:hover:not(:disabled) {
  background: #369970;
}

.md-toolbar button.save-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.divider {
  width: 1px;
  height: 16px;
  background: #eee;
  margin: 0 0.15rem;
}

.editor-content {
  flex: 1;
  padding: 1rem;
  overflow-y: auto;
  min-height: 200px;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

/* 编辑器内容最大宽度 - 默认520px */
.editor-content :deep(.md-editor-content) {
  max-width: 520px;
  margin: 0 auto;
}

/* 无最大宽度限制 */
.editor-content.no-max-width :deep(.md-editor-content) {
  max-width: 100%;
  margin: 0;
}

/* 图片样式 */
.editor-content :deep(img) {
  max-width: 100%;
  height: auto;
  cursor: zoom-in;
  border-radius: 4px;
}

/* 图片预览弹窗 */
.image-preview-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  padding: 2rem;
}

.image-preview-container {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
}

.image-preview-img {
  max-width: 100%;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 4px;
}

.image-preview-close {
  position: absolute;
  top: -40px;
  right: 0;
  background: transparent;
  border: none;
  color: #fff;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s;
}

.image-preview-close:hover {
  opacity: 0.8;
}

/* 确认弹框 */
.confirm-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
}

.confirm-dialog {
  background: #fff;
  border-radius: 12px;
  padding: 1.5rem;
  width: 90%;
  max-width: 360px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  animation: scaleIn 0.2s ease;
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.confirm-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 0.75rem;
}

.confirm-message {
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 1.5rem;
  line-height: 1.5;
}

.confirm-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
}

.confirm-actions button {
  padding: 0.5rem 1.25rem;
  border-radius: 6px;
  font-size: 0.9rem;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
}

.btn-cancel {
  background: #f5f5f5;
  color: #666;
}

.btn-cancel:hover {
  background: #e8e8e8;
}

.btn-confirm {
  background: #ff6b6b;
  color: #fff;
}

.btn-confirm:hover {
  background: #ff5252;
}

.editor-content::-webkit-scrollbar {
  display: none;
}

/* 词条标记样式 */
:deep(.term-mark) {
  background: #e3f2fd;
  border-bottom: 2px solid #1976d2;
  cursor: pointer;
  padding: 0 2px;
  border-radius: 2px;
}

:deep(.term-mark:hover) {
  background: #bbdefb;
}

/* 右键菜单 */
.editor-context-menu {
  position: fixed;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  padding: 0.25rem 0;
  min-width: 120px;
}

.context-menu-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  font-size: 0.85rem;
  color: #333;
}

.context-menu-item:hover {
  background: #f5f5f5;
}

/* 抽屉样式 */
.term-drawer-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 2000;
}

.term-drawer {
  position: fixed;
  top: 0;
  right: 0;
  width: 560px;
  height: 100vh;
  background: #fff;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  transform: translateX(100%);
  transition: transform 0.3s ease;
}

.term-drawer.drawer-open {
  transform: translateX(0);
}

.term-drawer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e8e8e8;
}

.term-drawer-header h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-right: 1rem;
}

.term-drawer-body {
  flex: 1;
  padding: 1.25rem;
  overflow-y: auto;
}

.term-drawer-body .form-group {
  margin-bottom: 1rem;
}

.term-drawer-body label {
  display: block;
  font-size: 0.85rem;
  color: #666;
  margin-bottom: 0.5rem;
}

.form-input {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 0.9rem;
  box-sizing: border-box;
  background: #f5f5f5;
}

.form-input:focus {
  outline: none;
  border-color: #42b883;
}

.term-drawer-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  border-top: 1px solid #e8e8e8;
}

.term-drawer-footer.footer-right-only {
  justify-content: flex-end;
}

.footer-right {
  display: flex;
  gap: 0.75rem;
}

.btn-danger {
  padding: 0.5rem 1.25rem;
  border: 1px solid #ff4d4f;
  background: #fff;
  color: #ff4d4f;
  border-radius: 4px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-danger:hover {
  background: #ff4d4f;
  color: #fff;
}

.term-editor-instance {
  flex: 1;
  min-height: 0;
}

.drawer-close-btn {
  background: none;
  border: none;
  padding: 0.25rem;
  cursor: pointer;
  color: #999;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
}

.drawer-close-btn:hover {
  background: #f5f5f5;
  color: #333;
}

.btn-secondary {
  padding: 0.5rem 1.25rem;
  border: 1px solid #e0e0e0;
  background: #fff;
  color: #666;
  border-radius: 4px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-secondary:hover {
  background: #f5f5f5;
}

.btn-primary {
  padding: 0.5rem 1.25rem;
  border: none;
  background: #42b883;
  color: #fff;
  border-radius: 4px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-primary:hover {
  background: #369f6e;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.view-content {
  font-size: 0.95rem;
  line-height: 1.8;
  color: #333;
  white-space: pre-wrap;
}

.term-actions button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-loading {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid #fff;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.editor-content :deep(.md-editor-content) {
  outline: none;
  min-height: 100%;
}

.editor-content :deep(.md-editor-content p) {
  margin: 0.5rem 0;
  line-height: 1.6;
}

.editor-content :deep(.md-editor-content h1) {
  font-size: 1.5rem;
  margin: 1rem 0 0.5rem;
}

.editor-content :deep(.md-editor-content h2) {
  font-size: 1.25rem;
  margin: 0.875rem 0 0.5rem;
}

.editor-content :deep(.md-editor-content h3) {
  font-size: 1.1rem;
  margin: 0.75rem 0 0.5rem;
}

.editor-content :deep(.md-editor-content ul),
.editor-content :deep(.md-editor-content ol) {
  margin: 0.5rem 0;
  padding-left: 1.5rem;
}

.editor-content :deep(.md-editor-content blockquote) {
  border-left: 3px solid #42b883;
  padding-left: 1rem;
  margin: 0.5rem 0;
  color: #666;
}

.editor-content :deep(.md-editor-content pre) {
  background: #f5f5f5;
  padding: 0.75rem;
  border-radius: 4px;
  overflow-x: auto;
}

.editor-content :deep(.md-editor-content img) {
  max-width: 100%;
  border-radius: 4px;
}
</style>
